// Authentication slice

import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { User, LoginRequest, LoginResponse, ApiError } from '@/types';
import { authApi, TokenManager } from '@/utils/api';
import i18n from '@/i18n';
import { logger } from '@/utils/logger';

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,
};

// Async thunks
export const login = createAsyncThunk<
  LoginResponse,
  LoginRequest,
  { rejectValue: ApiError }
>(
  'auth/login',
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await authApi.login(credentials);
      
      // Store tokens
      TokenManager.setTokens(response.access_token, response.refresh_token);
      
      return response;
    } catch (error: any) {
      // Extract error details
      const apiError: ApiError = error?.response?.data || error || {
        detail: 'Sign-in error',
      };
      logger.error('Login error:', apiError);
      return rejectWithValue(apiError);
    }
  }
);

export const logout = createAsyncThunk<
  void,
  void,
  { rejectValue: ApiError }
>(
  'auth/logout',
  async (_, { rejectWithValue }) => {
    try {
      const refreshToken = TokenManager.getRefreshToken();
      if (refreshToken) {
        await authApi.logout(refreshToken);
      }
    } catch (error: any) {
      // Continue with logout even if API call fails
      logger.warn('Logout API call failed:', error);
    } finally {
      // Always clear tokens
      TokenManager.clearTokens();
    }
  }
);

export const getCurrentUser = createAsyncThunk<
  User,
  void,
  { rejectValue: ApiError }
>(
  'auth/getCurrentUser',
  async (_, { rejectWithValue }) => {
    try {
      const user = await authApi.getCurrentUser();
      if (user === null) {
        return rejectWithValue({ detail: 'Unauthorized', status_code: 401 } as ApiError);
      }
      return user;
    } catch (error: any) {
      return rejectWithValue(error);
    }
  }
);

export const changePassword = createAsyncThunk<
  void,
  { current_password: string; new_password: string },
  { rejectValue: ApiError }
>(
  'auth/changePassword',
  async (passwordData, { rejectWithValue }) => {
    try {
      await authApi.changePassword(passwordData);
    } catch (error: any) {
      return rejectWithValue(error);
    }
  }
);

// Slice
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setUser: (state, action: PayloadAction<User>) => {
      state.user = action.payload;
      state.isAuthenticated = true;
    },
    clearAuth: (state) => {
      state.user = null;
      state.isAuthenticated = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Login
    builder
      .addCase(login.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload.user;
        state.isAuthenticated = true;
        state.error = null;
        
        // Load user's preferred language if available
        if (action.payload.user.preferred_language) {
          i18n.changeLanguage(action.payload.user.preferred_language);
          localStorage.setItem('i18nextLng', action.payload.user.preferred_language);
        }
      })
      .addCase(login.rejected, (state, action) => {
        state.isLoading = false;
        state.error = (action.payload as ApiError)?.detail || 'Sign-in error';
        state.isAuthenticated = false;
        state.user = null;
      });

    // Logout
    builder
      .addCase(logout.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(logout.fulfilled, (state) => {
        state.isLoading = false;
        state.user = null;
        state.isAuthenticated = false;
        state.error = null;
      })
      .addCase(logout.rejected, (state) => {
        state.isLoading = false;
        state.user = null;
        state.isAuthenticated = false;
        state.error = null;
      });

    // Get current user
    builder
      .addCase(getCurrentUser.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getCurrentUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload;
        state.isAuthenticated = true;
        state.error = null;
        
        // Load user's preferred language if available
        if (action.payload.preferred_language) {
          i18n.changeLanguage(action.payload.preferred_language);
          localStorage.setItem('i18nextLng', action.payload.preferred_language);
        }
      })
      .addCase(getCurrentUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = (action.payload as ApiError)?.detail || 'Failed to load user data';
        state.isAuthenticated = false;
        state.user = null;
        // Clear tokens if user fetch fails
        TokenManager.clearTokens();
      });

    // Change password
    builder
      .addCase(changePassword.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(changePassword.fulfilled, (state) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(changePassword.rejected, (state, action) => {
        state.isLoading = false;
        state.error = (action.payload as ApiError)?.detail || 'Failed to change password';
      });
  },
});

export const { clearError, setUser, clearAuth } = authSlice.actions;
export default authSlice.reducer;

